import { useEffect, useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Field } from '../../components/molecules/Field'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'
import type {
  AddPricingInput,
  ManufacturingPricingDetailDto,
  ManufacturingPricingSummaryDto,
  SupplierPricingDto,
  UpdatePricingInput
} from '../../../viewmodels/useManufacturingPricing'

const MAX_PRICE_PAISA = 1_00_00_000

interface Props {
  readonly target: ManufacturingPricingSummaryDto | null
  readonly onClose: () => void
  readonly fetchDetail: (
    rawMaterialId: string
  ) => Promise<{ ok: true; data: ManufacturingPricingDetailDto } | { ok: false; message: string }>
  readonly addPricing: (
    rawMaterialId: string,
    input: AddPricingInput
  ) => Promise<{ ok: true; data: SupplierPricingDto } | { ok: false; field: string | null; message: string }>
  readonly updatePricing: (
    rawMaterialId: string,
    pricingId: string,
    patch: UpdatePricingInput
  ) => Promise<{ ok: true; data: SupplierPricingDto } | { ok: false; field: string | null; message: string }>
  readonly deletePricing: (
    rawMaterialId: string,
    pricingId: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
}

type DetailState =
  | { status: 'loading' }
  | { status: 'ready'; data: ManufacturingPricingDetailDto }
  | { status: 'error'; message: string }

type EditMode =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; row: SupplierPricingDto }

/* ───────────────── formatting + parsing helpers ───────────────── */

function paisaToRupeeString(p: number | null): string {
  if (p === null) return ''
  return (p / 100).toFixed(2)
}
function formatPaisa(p: number | null): string {
  if (p === null) return '—'
  return `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
function parseRupees(raw: string, label: string): number | null {
  const t = raw.trim()
  if (t === '') return null
  const n = Number(t)
  if (!Number.isFinite(n) || n < 0) throw new Error(`${label} must be a non-negative number`)
  const p = Math.round(n * 100)
  if (p > MAX_PRICE_PAISA) throw new Error(`${label} must be ≤ ₹${(MAX_PRICE_PAISA / 100).toLocaleString('en-IN')}`)
  return p
}

/* ───────────────── modal ───────────────── */

export function ManufacturingPricingDetailModal({
  target,
  onClose,
  fetchDetail,
  addPricing,
  updatePricing,
  deletePricing
}: Props): JSX.Element {
  const [detail, setDetail] = useState<DetailState | null>(null)
  const [edit, setEdit] = useState<EditMode>({ kind: 'closed' })
  const [opError, setOpError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Load (or reset) the detail payload whenever the target changes.
  useEffect(() => {
    if (target === null) {
      setDetail(null)
      setEdit({ kind: 'closed' })
      setOpError(null)
      return
    }
    let cancelled = false
    setDetail({ status: 'loading' })
    setOpError(null)
    void fetchDetail(target.raw_material_id).then((res) => {
      if (cancelled) return
      if (res.ok) setDetail({ status: 'ready', data: res.data })
      else setDetail({ status: 'error', message: res.message })
    })
    return () => {
      cancelled = true
    }
  }, [target, fetchDetail])

  // Reload detail after any mutation succeeds so addable_suppliers +
  // supplier_prices stay accurate.
  const reloadDetail = async (): Promise<void> => {
    if (target === null) return
    const res = await fetchDetail(target.raw_material_id)
    if (res.ok) setDetail({ status: 'ready', data: res.data })
    else setDetail({ status: 'error', message: res.message })
  }

  const handleAddSubmit = async (input: AddPricingInput): Promise<{ ok: true } | { ok: false; field: string | null; message: string }> => {
    if (target === null) return { ok: false, field: null, message: 'No target' }
    const res = await addPricing(target.raw_material_id, input)
    if (!res.ok) return res
    await reloadDetail()
    return { ok: true }
  }

  const handleEditSubmit = async (
    pricingId: string,
    patch: UpdatePricingInput
  ): Promise<{ ok: true } | { ok: false; field: string | null; message: string }> => {
    if (target === null) return { ok: false, field: null, message: 'No target' }
    const res = await updatePricing(target.raw_material_id, pricingId, patch)
    if (!res.ok) return res
    await reloadDetail()
    return { ok: true }
  }

  const handleDelete = async (row: SupplierPricingDto): Promise<void> => {
    if (target === null) return
    if (typeof window !== 'undefined' && !window.confirm(`Remove ${row.supplier_name}'s pricing for this material?`)) return
    setDeletingId(row.id)
    setOpError(null)
    const res = await deletePricing(target.raw_material_id, row.id)
    setDeletingId(null)
    if (!res.ok) {
      setOpError(res.message)
      return
    }
    await reloadDetail()
  }

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={
        detail?.status === 'ready'
          ? `Pricing — ${detail.data.raw_material_name}`
          : 'Pricing'
      }
      maxWidth="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        {detail === null || detail.status === 'loading' ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : detail.status === 'error' ? (
          <Alert tone="error">{detail.message}</Alert>
        ) : (
          <DetailBody
            data={detail.data}
            edit={edit}
            setEdit={setEdit}
            opError={opError}
            deletingId={deletingId}
            onDelete={(row) => void handleDelete(row)}
            onAddSubmit={handleAddSubmit}
            onEditSubmit={handleEditSubmit}
          />
        )}
      </div>
    </Modal>
  )
}

function DetailBody({
  data,
  edit,
  setEdit,
  opError,
  deletingId,
  onDelete,
  onAddSubmit,
  onEditSubmit
}: {
  readonly data: ManufacturingPricingDetailDto
  readonly edit: EditMode
  readonly setEdit: (m: EditMode) => void
  readonly opError: string | null
  readonly deletingId: string | null
  readonly onDelete: (row: SupplierPricingDto) => void
  readonly onAddSubmit: (input: AddPricingInput) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly onEditSubmit: (pricingId: string, patch: UpdatePricingInput) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}): JSX.Element {
  const { can } = useAuth()
  const canAddPerm = can(SCREENCODES.MANUFACTURINGPRICING, 'add')
  const canAdd = data.addable_suppliers.length > 0
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <p className="text-body-sm text-text-secondary">
          Prices are per <span className="font-mono">{data.raw_material_unit_label}</span>.
        </p>
        {edit.kind === 'closed' && canAddPerm ? (
          <Button
            size="sm"
            onClick={() => setEdit({ kind: 'add' })}
            disabled={!canAdd}
            title={canAdd ? '' : 'All active suppliers are already priced. Add a supplier first, or edit an existing row.'}
          >
            Add supplier price
          </Button>
        ) : null}
      </div>

      {opError !== null ? <Alert tone="error">{opError}</Alert> : null}

      {edit.kind === 'add' ? (
        <PricingForm
          mode="add"
          addableSuppliers={data.addable_suppliers}
          initial={null}
          onCancel={() => setEdit({ kind: 'closed' })}
          onAddSubmit={async (input) => {
            const r = await onAddSubmit(input)
            if (r.ok) setEdit({ kind: 'closed' })
            return r
          }}
          onEditSubmit={async () => ({ ok: false, field: null, message: 'unreachable' })}
        />
      ) : edit.kind === 'edit' ? (
        <PricingForm
          mode="edit"
          addableSuppliers={[]}
          initial={edit.row}
          onCancel={() => setEdit({ kind: 'closed' })}
          onAddSubmit={async () => ({ ok: false, field: null, message: 'unreachable' })}
          onEditSubmit={async (patch) => {
            const r = await onEditSubmit(edit.row.id, patch)
            if (r.ok) setEdit({ kind: 'closed' })
            return r
          }}
        />
      ) : null}

      {data.supplier_prices.length === 0 ? (
        <div className="text-center py-8 border border-border rounded-md bg-parchment/30">
          <p className="font-body text-body-sm text-text-secondary">
            No supplier prices yet — click <span className="font-medium">Add supplier price</span>.
          </p>
        </div>
      ) : (
        <PricesTable
          rows={data.supplier_prices}
          onEdit={(row) => setEdit({ kind: 'edit', row })}
          onDelete={onDelete}
          deletingId={deletingId}
          editingId={edit.kind === 'edit' ? edit.row.id : null}
        />
      )}
    </>
  )
}

function PricesTable({
  rows,
  onEdit,
  onDelete,
  deletingId,
  editingId
}: {
  readonly rows: ReadonlyArray<SupplierPricingDto>
  readonly onEdit: (row: SupplierPricingDto) => void
  readonly onDelete: (row: SupplierPricingDto) => void
  readonly deletingId: string | null
  readonly editingId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.MANUFACTURINGPRICING, 'edit')
  const canDelete = can(SCREENCODES.MANUFACTURINGPRICING, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Supplier</Th>
            <Th className="text-right">Seller</Th>
            <Th className="text-right">Discounted</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border">
              <Td>{r.supplier_name}</Td>
              <Td className="text-right whitespace-nowrap">{formatPaisa(r.seller_price_paisa)}</Td>
              <Td className="text-right whitespace-nowrap">
                {r.discounted_price_paisa === null
                  ? <span className="text-text-secondary">none</span>
                  : formatPaisa(r.discounted_price_paisa)}
              </Td>
              <Td className="text-right whitespace-nowrap">
                {canEdit ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onEdit(r)}
                      disabled={editingId === r.id}
                    >
                      Edit
                    </Button>{' '}
                  </>
                ) : null}
                {canDelete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(r)}
                    disabled={deletingId === r.id}
                  >
                    {deletingId === r.id ? 'Removing…' : 'Remove'}
                  </Button>
                ) : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}

/* ───────────────── add/edit form ───────────────── */

interface FormState {
  supplierId: string
  seller: string
  discounted: string
}

function PricingForm({
  mode,
  addableSuppliers,
  initial,
  onCancel,
  onAddSubmit,
  onEditSubmit
}: {
  readonly mode: 'add' | 'edit'
  readonly addableSuppliers: ReadonlyArray<{ readonly id: string; readonly name: string }>
  readonly initial: SupplierPricingDto | null
  readonly onCancel: () => void
  readonly onAddSubmit: (input: AddPricingInput) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly onEditSubmit: (patch: UpdatePricingInput) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}): JSX.Element {
  const initialFromRow: FormState =
    initial === null
      ? { supplierId: addableSuppliers[0]?.id ?? '', seller: '', discounted: '' }
      : {
          supplierId: initial.supplier_id,
          seller: paisaToRupeeString(initial.seller_price_paisa),
          discounted: initial.discounted_price_paisa === null ? '' : paisaToRupeeString(initial.discounted_price_paisa)
        }
  const [state, setState] = useState<FormState>(initialFromRow)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (): Promise<void> => {
    setFieldError(null)
    setFormError(null)
    let sellerPaisa: number | null
    let discountedPaisa: number | null
    try { sellerPaisa = parseRupees(state.seller, 'Seller price') } catch (e) { setFieldError({ field: 'seller', message: msg(e) }); return }
    try { discountedPaisa = parseRupees(state.discounted, 'Discounted price') } catch (e) { setFieldError({ field: 'discounted', message: msg(e) }); return }
    if (discountedPaisa !== null && sellerPaisa !== null && discountedPaisa > sellerPaisa) {
      setFieldError({ field: 'discounted', message: 'Discounted price must not exceed seller price' })
      return
    }

    setSubmitting(true)
    if (mode === 'add') {
      if (state.supplierId === '') {
        setFieldError({ field: 'supplierId', message: 'Pick a supplier' })
        setSubmitting(false)
        return
      }
      if (sellerPaisa === null) { setFieldError({ field: 'seller', message: 'Seller price is required' }); setSubmitting(false); return }
      const r = await onAddSubmit({
        supplier_id: state.supplierId,
        seller_price_paisa: sellerPaisa,
        discounted_price_paisa: discountedPaisa
      })
      setSubmitting(false)
      if (!r.ok) handleServerError(r, setFieldError, setFormError)
      return
    }
    // edit
    if (initial === null) return
    const draft: {
      seller_price_paisa?: number
      discounted_price_paisa?: number | null
    } = {}
    if (sellerPaisa !== null && sellerPaisa !== initial.seller_price_paisa) draft.seller_price_paisa = sellerPaisa
    const initialDiscounted = initial.discounted_price_paisa
    if (discountedPaisa !== initialDiscounted) draft.discounted_price_paisa = discountedPaisa
    const patch: UpdatePricingInput = draft
    if (Object.keys(patch).length === 0) {
      setFormError('Nothing changed — adjust at least one field, or click Cancel.')
      setSubmitting(false)
      return
    }
    const r = await onEditSubmit(patch)
    setSubmitting(false)
    if (!r.ok) handleServerError(r, setFieldError, setFormError)
  }

  return (
    <div className="border border-border rounded-md p-4 bg-parchment/30 space-y-3">
      <h3 className="font-heading text-h4 text-charcoal">
        {mode === 'add' ? 'Add supplier price' : `Edit ${initial?.supplier_name ?? ''}`}
      </h3>
      {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

      <div className="grid grid-cols-2 gap-3">
        {mode === 'add' ? (
          <Field label="Supplier" error={fieldError?.field === 'supplierId' ? fieldError.message : null}>
            <Select
              value={state.supplierId}
              hasError={fieldError?.field === 'supplierId'}
              onChange={(e) => setState((s) => ({ ...s, supplierId: e.target.value }))}
            >
              {addableSuppliers.length === 0 ? (
                <option value="">No suppliers available</option>
              ) : (
                addableSuppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))
              )}
            </Select>
          </Field>
        ) : (
          <div className="col-span-2">
            <span className="block text-body-sm text-text-secondary">Supplier</span>
            <span className="block font-body text-body-sm text-text-primary mt-1">{initial?.supplier_name}</span>
          </div>
        )}

        <Field label="Seller price (₹)" error={fieldError?.field === 'seller' ? fieldError.message : null}>
          <Input
            value={state.seller}
            hasError={fieldError?.field === 'seller'}
            onChange={(e) => setState((s) => ({ ...s, seller: e.target.value }))}
            placeholder="e.g. 250.00"
            inputMode="decimal"
          />
        </Field>

        <Field label="Discounted price (₹)" error={fieldError?.field === 'discounted' ? fieldError.message : null}>
          <Input
            value={state.discounted}
            hasError={fieldError?.field === 'discounted'}
            onChange={(e) => setState((s) => ({ ...s, discounted: e.target.value }))}
            placeholder="Leave blank for no discount"
            inputMode="decimal"
          />
        </Field>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Saving…' : mode === 'add' ? 'Add' : 'Save'}
        </Button>
      </div>
    </div>
  )
}


function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function handleServerError(
  r: { ok: false; field: string | null; message: string },
  setFieldError: (e: { field: keyof FormState; message: string } | null) => void,
  setFormError: (m: string | null) => void
): void {
  const map: Record<string, keyof FormState> = {
    seller_price_paisa: 'seller',
    discounted_price_paisa: 'discounted',
    supplier_id: 'supplierId'
  }
  const formField = r.field !== null ? map[r.field] : undefined
  if (formField !== undefined) {
    setFieldError({ field: formField, message: r.message })
  } else {
    setFormError(r.message)
  }
}
